import { useMutation, useQuery } from "@apollo/client";
import {
  Checkbox,
  Divider,
  Fab,
  FormControlLabel,
  makeStyles,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

import { Skeleton } from "@material-ui/lab";
import lodash from "lodash";
import { useSnackbar } from "notistack";
import React from "react";
import { useHistory, useParams } from "react-router";
import { CompanyDialog } from "../../components/Company";
import DeleteDialog from "../../components/DeleteDialog";
import LoadingError from "../../components/LoadingError";
import { LocationList } from "../../components/Location";
import LocationDialog from "../../components/Location/LocationDialog";
import TextBox from "../../components/TextBox";
import { DEFAULT_LIMIT, ROUTES } from "../../constants";
import { UPDATE_COMPANY } from "../../graphql/mutations";
import { UpdateCompanyMutation } from "../../graphql/mutations/updateCompany";
import {
  GET_COMPANY,
  GET_COMPANY_LOCATIONS
} from "../../graphql/queries";
import { GetCompanyQuery } from "../../graphql/queries/getCompany";
import { GetCompanyLocationsQuery } from "../../graphql/queries/getCompanyLocations";
import {
  GetCompanyLocationsQueryVariables,
  GetCompanyQueryVariables,
  UpdateCompanyMutationVariables
} from "../../graphql/types";
import useSiteTitle from "../../hooks/useSiteTitle";
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },

  contentRoot: {
    display: "flex",
    flex: 1,
    padding: "40px 60px",
    width: "100%",
    flexDirection: "column",
    marginTop: 40,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  detailsHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",

    flex: 1,
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
  },
  content: {
    marginBottom: theme.spacing(2),
  },
}));

const ComanpyDetails: React.FC = () => {
  const [searchValue, setSearchValue] = React.useState<string | undefined>("");
  const [showDialog, setShowDialog] = React.useState<string>("");
  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);

  const [showLocationDialog, setShowLocationDialog] =
    React.useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const { id = "" } = useParams<{
    id: string;
  }>();
  const classes = useStyles();
  const history = useHistory();

  const { data, loading, error } = useQuery<
    GetCompanyQuery,
    GetCompanyQueryVariables
  >(GET_COMPANY, {
    variables: {
      id,
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden des Auftraggeber", {
        variant: "error",
      });
    },
  });
  const [deactivate, { loading: deactivating }] = useMutation<
    UpdateCompanyMutation,
    UpdateCompanyMutationVariables
  >(UPDATE_COMPANY, {
    update(cache) {
      try {
        const normalizedId = cache.identify({
          id,
          __typename: "Company",
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      } catch (error) {
        console.log(error);
      }
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim löschen des Auftraggebers", {
        variant: "error",
      });
    },
    onCompleted() {
      enqueueSnackbar("Auftraggeber wurde gelöscht", { variant: "warning" });
      history.replace(ROUTES.COMPANIES);
    },
  });

  const {
    data: locationsData,
    loading: load,
    error: locationError,
    fetchMore: fetchMoreLocations,
  } = useQuery<GetCompanyLocationsQuery, GetCompanyLocationsQueryVariables>(
    GET_COMPANY_LOCATIONS,
    {
      variables: {
        id,
        name: searchValue,
        locationId: searchValue,
      },
      onError(error) {
        console.log(error);
        enqueueSnackbar("Fehler beim laden der Standorte", {
          variant: "error",
        });
      },
    }
  );
  const { getCompany } = data || {};
  const { getCompany: locationCompany } = locationsData || {
    getCompany: { locations: { items: [] } },
  };

  const {
    name,
    settings: { hasSelfTests = false, defaultSelfTests = "" } = {},
  } = getCompany || {};
  //@ts-ignore
  const { locations: { items, nextToken } = { items: [], nextToken: null } } =
    locationCompany || {};

  useSiteTitle(`Auftraggeber - ${name || ""}`);
  const _handleClientSearch = lodash.debounce(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchValue(event.target.value);
    },
    500
  );
  const _handleCleareSearch = () => {
    setSearchValue("");
  };
  const _handleCloseDialog = () => {
    setShowDialog("");
  };
  const _openDialog = () => {
    setShowDialog(id);
  };
  const _openLocationDialog = () => {
    setShowLocationDialog(true);
  };
  const _handleCloseLocationDialog = () => {
    setShowLocationDialog(false);
  };
  const _closeDeactivateDialog = () => {
    setShowDeleteDialog(false);
  };
  const _openDeactivateDialog = () => {
    setShowDeleteDialog(true);
  };
  return (
    <div className={classes.root}>
      <DeleteDialog
        open={showDeleteDialog}
        loading={deactivating}
        onSubmit={() => {
          deactivate({ variables: { input: { id, deactivated: true } } });
        }}
        handleClose={_closeDeactivateDialog}
        object="Auftraggeber"
      />
      <CompanyDialog
        open={!!showDialog}
        id={showDialog}
        initialValues={{
          name: name || "",
          hasSelfTests,
          defaultSelfTests: defaultSelfTests || 2,
        }}
        handleClose={_handleCloseDialog}
      />
      <LocationDialog
        open={showLocationDialog}
        handleClose={_handleCloseLocationDialog}
        companyId={id}
      />

      <div className={classes.header}>
        <Typography variant="h3">
          {!loading && `Auftraggeber ${name}`}
          {loading && <Skeleton variant="text" width={700} height={80} />}
        </Typography>
        <div>
          <Tooltip title="Auftraggeber löschen" style={{ marginRight: 20 }}>
            <Fab
              aria-label="update company"
              size={"small"}
              disabled={loading}
              onClick={_openDeactivateDialog}
              color={"primary"}
            >
              <DeleteIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="Auftraggeber bearbeiten">
            <Fab
              aria-label="update company"
              size={"small"}
              disabled={loading}
              onClick={_openDialog}
              color={"primary"}
            >
              <EditIcon />
            </Fab>
          </Tooltip>
        </div>
      </div>
      {!loading && !!error && (
        <LoadingError text="Der Auftraggeber konnte nicht geladen werden" />
      )}
      {!error && loading && (
        <>
          <Skeleton variant="rect" height={600} style={{ marginTop: 20 }} />
        </>
      )}
      {!loading && !error && (
        <Paper className={classes.contentRoot}>
          <div className={classes.detailsHeader}>
            <Typography variant="h5">Details</Typography>
          </div>
          <Divider />
          <div className={classes.content}>
            <TextBox label="Name" value={name || ""} bordered={false} />
            <Typography variant="overline">Einstellungen</Typography>
            <Divider />

            <div>
              <TextBox
                label="Selbttestanzahl"
                value={`${defaultSelfTests || 0}`}
                bordered={false}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasSelfTests}
                    color="primary"
                    disabled
                    inputProps={{ "aria-label": "selftests checkbox" }}
                  />
                }
                label="Selbttestausgabe aktiv"
              />
            </div>
          </div>
        </Paper>
      )}
      {!locationError && (
        <LocationList
          items={items || []}
          onAddLocation={_openLocationDialog}
          company={name || ""}
          loading={load}
          onClearSearch={_handleCleareSearch}
          onSearchChange={_handleClientSearch}
          searchValue={searchValue}
          hasMore={!!nextToken}
          loadMore={() => {
            if (nextToken) {
              fetchMoreLocations({
                variables: {
                  id,
                  name: searchValue,
                  locationId: searchValue,
                  limit: DEFAULT_LIMIT,
                  nextToken,
                },
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default ComanpyDetails;
